import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Form, Skeleton, Typography } from 'antd';

import { PageProps } from '@/types/PageProps';
import { SaveButton } from '@/components/_common';

import {
  MailConfigurationFormValues,
  SenderType,
} from '@/core/domain/mail/MailConfigurationController/MailConfigurationController.types';
import {
  useMailConfiguration,
  useMailConfigurationCreate,
  useMailConfigurationUpdate,
  useMailConfigurationVerify,
} from '@/core/domain/mail/MailConfigurationController/MailConfigurationController.hooks';
import { getUserFromLocalStorage } from '@/utils/user';
import mailBase64ToUrl from '@/utils/functions/mailBase64ToUrl';
import { useWindowSize } from '@/hooks';
import EmailSettingsFooterActions from '@/components/Settings/Email/EmailSettingsFooterActions';
import EmailSettingsForm from '@/components/Settings/Email/EmailSettingsForm';

const { Title } = Typography;

function EmailSettingsProfilePage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<MailConfigurationFormValues>();
  const { userEmail } = getUserFromLocalStorage();
  const { data, dataUpdatedAt, isLoading, remove } = useMailConfiguration({
    sender: SenderType.user,
  });

  const isExistingMail = !!data?.id;

  const mutationCreateConfiguration = useMailConfigurationCreate();
  const mutationUpdateConfiguration = useMailConfigurationUpdate();
  const mailConfigurationVerify = useMailConfigurationVerify();

  const [uploading, setUploading] = useState(false);
  const { userId } = getUserFromLocalStorage();

  const { width } = useWindowSize();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ ...data, signature: data.signature ?? '' });
    } else if (!data && !isLoading) {
      form.setFieldValue('mail', userEmail);
    }
  }, [dataUpdatedAt]);

  useEffect(() => {
    return () => remove();
  }, []);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    let newMessage = '';

    try {
      newMessage = await mailBase64ToUrl(values.signature, setUploading);

      if (data?.id) {
        await mutationUpdateConfiguration.mutateAsync({
          id: data.id,
          ...values,
          sender: SenderType.user,
          senderId: Number(userId),
          signature: newMessage,
        });
      } else {
        await mutationCreateConfiguration.mutateAsync({
          ...values,
          sender: SenderType.user,
          senderId: Number(userId),
          signature: newMessage,
        });
      }
    } finally {
      form.setFieldValue('signature', newMessage);
    }
  };

  const handleVerifyEmail = async () => {
    const values = await form.validateFields();

    await mailConfigurationVerify.mutateAsync({
      id: Number(data?.id),
      mail: values?.mail,
      password: values?.password,
      port: values?.port,
      smtp: values?.smtp,
      ssl: values?.ssl,
      tls: values?.tls,
    });
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <div className="page">
        <div className="page-header">
          <Title level={4}>{intl.formatMessage({ id: route.name })}</Title>

          {!(width < 992) && (
            <SaveButton
              onClick={handleSubmit}
              loading={
                mutationCreateConfiguration.isLoading ||
                mutationUpdateConfiguration.isLoading ||
                uploading
              }
            >
              {intl.formatMessage({
                id: 'commons.form.button.save',
              })}
            </SaveButton>
          )}
        </div>

        <Skeleton loading={isLoading}>
          <EmailSettingsForm
            form={form}
            isExistingMail={isExistingMail}
            isProfile
          />

          <EmailSettingsFooterActions
            handleSubmit={handleSubmit}
            saveLoading={
              mutationCreateConfiguration.isLoading ||
              mutationUpdateConfiguration.isLoading ||
              uploading
            }
            handleVerifyEmail={handleVerifyEmail}
            verifyEmailLoading={mailConfigurationVerify.isLoading}
          />
        </Skeleton>
      </div>
    </>
  );
}

export default EmailSettingsProfilePage;
